import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { booking, isLabNumberConflicts, type RollDetailsRequest } from '../api/booking';
import { lab } from '../api/lab';
import { isApiError } from '../api/client';
import { filmStocks, type FilmStock } from '../api/filmStocks';
import { queryKeys } from '../api/queryKeys';
import { useAdminCall } from '../hooks/useAdminCall';
import { useConfirm } from '../hooks/useConfirm';
import type { NegativesPlan, Roll } from '../types/roll';
import { UNKNOWN_FILM_STOCK } from '../utils/filmStocks';
import {
  checkRollFields,
  describeNegatives,
  describeRoll,
  filmLabel,
  labIsDone,
  PLAN_LABELS,
  stageLabel,
  UNKNOWN_FILM_STOCK_ID,
  type RollFieldProblems,
} from '../utils/rollDetails';
import { NegativesActions } from './NegativesActions';
import { RollDetailsFields, type RollFieldsValue } from './RollDetailsFields';
import styles from './RollDetails.module.css';

type RollWithDetails = Pick<Roll, 'roll_id' | 'roll_name'> & Partial<Roll>;

/** The stock a roll records, from the catalogue when it's there. */
function stockOf(roll: RollWithDetails, stocks: FilmStock[]): FilmStock | null {
  if (!roll.film_stock_id) return null;
  if (roll.film_stock_id === UNKNOWN_FILM_STOCK_ID) return UNKNOWN_FILM_STOCK;
  return (
    stocks.find((s) => s.stock_id === roll.film_stock_id) ?? {
      ...UNKNOWN_FILM_STOCK,
      stock_id: roll.film_stock_id,
      brand: roll.film_brand ?? '',
      name: roll.film_stock_name ?? roll.film_stock_id,
    }
  );
}

/** The form for correcting a roll's details. Rendered once the stocks are loaded. */
function DetailsForm({
  orderId,
  roll,
  stocks,
  onDone,
}: {
  orderId: string;
  roll: RollWithDetails;
  stocks: FilmStock[];
  onDone: (message: string) => void;
}) {
  const call = useAdminCall();
  const queryClient = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const numberRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<RollFieldsValue>({
    number: roll.lab_roll_number ?? '',
    stock: stockOf(roll, stocks),
    kind: roll.kind && roll.kind !== 'unknown' ? roll.kind : 'develop_and_scan',
    format: roll.format && roll.format !== 'other' ? roll.format : '',
    process: roll.process ?? '',
  });
  const [plan, setPlan] = useState<NegativesPlan | ''>(
    roll.negatives_plan && roll.negatives_plan !== 'unknown' ? roll.negatives_plan : '',
  );
  const [problems, setProblems] = useState<RollFieldProblems>({});
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (request: RollDetailsRequest) => call(() => booking.updateDetails(orderId, roll.roll_id, request)),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.rolls(orderId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.booking(orderId) });
      onDone(`Saved the details of ${saved.roll_name}.`);
    },
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    const found = checkRollFields(value);
    setProblems(found);
    setError(null);
    if (Object.keys(found).length > 0 || !value.stock || !value.format) {
      if (found.number) numberRef.current?.focus();
      return;
    }
    const request: RollDetailsRequest = {
      lab_roll_number: value.number.trim() || null,
      film_stock_id: value.stock.stock_id,
      kind: value.kind,
      process: value.process || null,
      format: value.format,
      ...(plan ? { negatives_plan: plan } : {}),
    };
    try {
      await save.mutateAsync(request);
    } catch (err) {
      if (isApiError(err, 409) && isLabNumberConflicts(err.body)) {
        const ok = await confirm({
          title: 'Lab number already used',
          message: `${err.body.error}.\n\nLabels come round again, so this may be right. Use it anyway?`,
          confirmLabel: 'Use it anyway',
          cancelLabel: 'Change the number',
        });
        if (!ok) {
          numberRef.current?.focus();
          return;
        }
        try {
          await save.mutateAsync({ ...request, allow_duplicate_numbers: true });
        } catch (again) {
          setError((isApiError(again) && again.serverMessage) || 'Saving failed. Try again.');
        }
        return;
      }
      setError((isApiError(err) && err.serverMessage) || 'Saving failed. Try again.');
    }
  }

  return (
    <form className={styles.form} onSubmit={submit} aria-label={`Details of ${roll.roll_name}`} noValidate>
      <RollDetailsFields
        value={value}
        onChange={(patch) => setValue((v) => ({ ...v, ...patch }))}
        errors={problems}
        stocks={stocks}
        numberRef={numberRef}
      />
      <label className={styles.planField}>
        <span className={styles.planLabel}>Negatives</span>
        <select className={styles.select} value={plan} onChange={(e) => setPlan(e.target.value as NegativesPlan | '')}>
          <option value="">As the order says</option>
          {Object.entries(PLAN_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </label>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <div className={styles.actions}>
        <button type="button" className={styles.secondaryBtn} onClick={() => onDone('')} disabled={save.isPending}>
          Cancel
        </button>
        <button type="submit" className={styles.primaryBtn} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save details'}
        </button>
      </div>
      {confirmDialog}
    </form>
  );
}

/**
 * A roll's booked-in details on its admin card: lab number, film, format,
 * service and stage, where its negatives are (with the parcel to track once
 * they're posted), and a form to correct them. A roll from before booking in
 * offers to add them, which starts tracking it.
 */
export function RollDetails({ orderId, roll }: { orderId: string; roll: RollWithDetails }) {
  const call = useAdminCall();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState('');
  const [stageError, setStageError] = useState<string | null>(null);
  const stocks = useQuery({
    queryKey: queryKeys.admin.filmStocks(),
    queryFn: () => call(filmStocks.list),
    enabled: editing,
  });
  const move = useMutation({
    mutationFn: (stage: 'developed' | 'received') => call(() => lab.setStage(orderId, roll.roll_id, stage)),
    onSuccess: (_roll, stage) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.rolls(orderId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.labQueue() });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.booking(orderId) });
      setStatus(stage === 'developed' ? `Marked ${roll.roll_name} developed.` : `Moved ${roll.roll_name} back to received.`);
    },
    onError: (e) => setStageError((isApiError(e) && e.serverMessage) || 'That didn’t save. Try again.'),
  });
  const canDevelop = roll.stage === 'received' && roll.kind !== 'scan_only';
  const canUndevelop = roll.stage === 'developed' && !roll.negatives_resolution;

  const booked = !!roll.stage;
  const film = filmLabel(roll);
  const stage = stageLabel(roll);
  const facts = [film, describeRoll(roll), stage].filter(Boolean).join(' · ');
  const negatives = roll.tracking ? describeNegatives(roll.tracking.negatives) : null;

  return (
    <div className={styles.details}>
      <div className={styles.summary}>
        <p className={styles.facts}>
          {roll.lab_roll_number ? (
            <>
              Lab no. <strong className={styles.number}>{roll.lab_roll_number}</strong>
              {facts && ` · ${facts}`}
            </>
          ) : booked ? (
            facts || 'Booked in'
          ) : (
            <>Not booked in{stage && ` · ${stage}`}</>
          )}
        </p>
        {negatives && (
          <p className={negatives.attention ? `${styles.negatives} ${styles.attention}` : styles.negatives}>
            Negatives: {negatives.text}
            {negatives.trackingUrl && (
              <>
                {' · '}
                <a href={negatives.trackingUrl} target="_blank" rel="noopener noreferrer" className={styles.track}>
                  Track parcel<span className={styles.srOnly}> (opens in a new tab)</span>
                </a>
              </>
            )}
          </p>
        )}
        {roll.special_instructions && (
          <p className={styles.instructions}>
            <strong>Special instructions:</strong> {roll.special_instructions}
          </p>
        )}
        {!editing && (
          <div className={styles.buttons}>
            <button
              type="button"
              className={styles.editBtn}
              onClick={() => {
                setStatus('');
                setEditing(true);
              }}
            >
              {booked ? 'Edit details' : 'Add details'}
            </button>
            {canDevelop && (
              <button
                type="button"
                className={styles.editBtn}
                disabled={move.isPending}
                onClick={() => {
                  setStageError(null);
                  move.mutate('developed');
                }}
              >
                Mark developed
              </button>
            )}
            {canUndevelop && (
              <button
                type="button"
                className={styles.editBtn}
                disabled={move.isPending}
                onClick={() => {
                  setStageError(null);
                  move.mutate('received');
                }}
              >
                Move back to received
              </button>
            )}
          </div>
        )}
        {stageError && (
          <p role="alert" className={styles.error}>
            {stageError}
          </p>
        )}
        {roll.tracking && !editing && (
          <NegativesActions
            orderId={orderId}
            rollId={roll.roll_id}
            rollName={roll.roll_name}
            negatives={roll.tracking.negatives}
            resolution={roll.negatives_resolution}
            plan={roll.negatives_plan}
            labDone={labIsDone(roll)}
            onDone={(_roll, message) => setStatus(message)}
          />
        )}
        <span role="status" className={styles.status}>
          {status}
        </span>
      </div>
      {editing &&
        (stocks.data ? (
          <DetailsForm
            orderId={orderId}
            roll={roll}
            stocks={stocks.data}
            onDone={(message) => {
              setEditing(false);
              setStatus(message);
            }}
          />
        ) : stocks.error ? (
          <p role="alert" className={styles.error}>
            Couldn't load the film stocks.
          </p>
        ) : (
          <p className={styles.loading}>Loading film stocks…</p>
        ))}
    </div>
  );
}
